import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useSidebarExpansion } from "../hooks/useSidebarExpansion";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import {
  getSportPath,
  getWorkspaceNavGroups,
  isNavItemActive,
} from "../utils/sportWorkspace";
import {
  getAdminNavGroups,
  isAdminNavItemActive,
  isAdminPath,
} from "../utils/adminWorkspace";
import { DESTINATION_ICONS } from "./sportIcons";
import Logo from "./Logo";
import MoreMenu from "./MoreMenu";
import SportSwitcher from "./SportSwitcher";
import { getMoreMenuNavItems } from "./moreMenuItems";
import AdminConsoleHeader from "./admin/AdminConsoleHeader";

export const DRAWER_WIDTH = 250;
export const COLLAPSED_DRAWER_WIDTH = 64;

const getSubheaderSx = (theme, groupId) => ({
  bgcolor: "transparent",
  color:
    groupId === "admin"
      ? theme.palette.warning.main
      : theme.palette.text.secondary,
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  lineHeight: 2.5,
  px: 2,
  pt: groupId === "explore" ? 0.5 : 1.5,
});

const getRailButtonSx = (isRailCollapsed) => ({
  minHeight: isRailCollapsed ? 52 : 48,
  justifyContent: isRailCollapsed ? "center" : "flex-start",
  px: isRailCollapsed ? 0 : undefined,
});

const getRailIconSx = (isRailCollapsed) => ({
  minWidth: isRailCollapsed ? 0 : 40,
  justifyContent: "center",
});

const wrapRailTooltip = (isRailCollapsed, title, node) =>
  isRailCollapsed ? (
    <Tooltip title={title} placement="right" arrow>
      {node}
    </Tooltip>
  ) : (
    node
  );

const SidebarFooterAction = ({
  isRailCollapsed,
  title,
  icon,
  onClick,
  selected = false,
  buttonProps = {},
}) => {
  const theme = useTheme();
  const color = theme.palette.text.secondary;
  const button = (
    <ListItemButton
      {...buttonProps}
      onClick={onClick}
      selected={selected}
      sx={{
        borderRadius: 1,
        mx: isRailCollapsed ? 1 : undefined,
        ...getRailButtonSx(isRailCollapsed),
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
        },
      }}
    >
      <ListItemIcon
        sx={{
          color,
          ...getRailIconSx(isRailCollapsed),
        }}
      >
        {icon}
      </ListItemIcon>
      {!isRailCollapsed && <ListItemText primary={title} />}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      <Tooltip
        title={title}
        placement="right"
        arrow
        disableHoverListener={!isRailCollapsed || selected}
      >
        {button}
      </Tooltip>
    </ListItem>
  );
};

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { logout, isAdmin } = useAuth();
  const { sport, destination, switchSport } = useSportWorkspace();
  const { isExpanded, handleMouseEnter, handleMouseLeave } =
    useSidebarExpansion({ hoverEnabled: !isMobile });
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [keepExpandedForMenu, setKeepExpandedForMenu] = useState(false);
  const moreOpen = Boolean(moreAnchorEl);
  const isRailCollapsed = !isMobile && !isExpanded && !keepExpandedForMenu;
  const adminConsole = isAdmin && isAdminPath(location.pathname);
  const menuGroups = adminConsole
    ? getAdminNavGroups()
    : getWorkspaceNavGroups(sport, isAdmin);
  const appHomePath = getSportPath(sport, "home");

  const handleMoreClick = (event) => {
    setKeepExpandedForMenu(isExpanded);
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
    setKeepExpandedForMenu(false);
  };

  const handleMoreNavigate = () => {
    handleMoreClose();
    if (isMobile) onClose();
  };

  const handleLogout = () => {
    handleMoreClose();
    logout();
    if (isMobile) onClose();
  };

  const handleSwitcherOpenChange = (menuOpen) => {
    setKeepExpandedForMenu(menuOpen ? isExpanded : false);
  };

  const handleSportSelect = (nextSport) => {
    switchSport(nextSport);
    if (isMobile) onClose();
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isRailCollapsed ? "center" : "flex-start",
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: isRailCollapsed ? 0 : 1.5,
          py: isRailCollapsed ? 0.5 : 0,
        }}
      >
        <Logo compact iconOnly sx={{ py: isRailCollapsed ? 0.75 : 2 }} />
      </Box>

      <Box
        sx={{
          px: isRailCollapsed ? 1 : 1.5,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {adminConsole ? (
          <AdminConsoleHeader
            isRailCollapsed={isRailCollapsed}
            homePath={appHomePath}
            onNavigate={isMobile ? onClose : undefined}
          />
        ) : (
          <SportSwitcher
            currentSport={sport}
            isRailCollapsed={isRailCollapsed}
            onSelect={handleSportSelect}
            onOpenChange={handleSwitcherOpenChange}
          />
        )}
      </Box>

      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            {groupIndex > 0 && isRailCollapsed && (
              <Divider sx={{ mx: 1.5, my: 0.5 }} />
            )}
            {!isRailCollapsed && (
              <ListSubheader
                component="div"
                disableSticky
                sx={getSubheaderSx(theme, group.id)}
              >
                {group.label}
              </ListSubheader>
            )}
            {group.items.map((item) => {
              const isActive = adminConsole
                ? isAdminNavItemActive(item.path, location.pathname)
                : location.pathname === item.path ||
                  isNavItemActive(item.id, destination);
              const Icon = DESTINATION_ICONS[item.id];
              const tooltip = item.comingSoon ? `${item.text} (soon)` : item.text;
              const emphasizeAdmin = item.isAdmin && !adminConsole;
              const listItemButton = (
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    ...getRailButtonSx(isRailCollapsed),
                    ...(emphasizeAdmin && {
                      borderLeft: `3px solid ${theme.palette.warning.main}`,
                      backgroundColor: "rgba(245, 158, 11, 0.08)",
                      "&:hover": {
                        backgroundColor: "rgba(245, 158, 11, 0.12)",
                      },
                    }),
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      color: theme.palette.text.primary,
                      borderLeft: `2px solid ${theme.palette.success.main}`,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "& .MuiListItemIcon-root": {
                        color: theme.palette.success.main,
                      },
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive
                        ? theme.palette.success.main
                        : emphasizeAdmin
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                      ...getRailIconSx(isRailCollapsed),
                    }}
                  >
                    {Icon ? <Icon /> : null}
                  </ListItemIcon>
                  {!isRailCollapsed && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        whiteSpace: "nowrap",
                        "& .MuiListItemText-primary": {
                          fontWeight: emphasizeAdmin ? 600 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  )}
                  {!isRailCollapsed && item.comingSoon && (
                    <Chip
                      label="Soon"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        bgcolor: "rgba(255, 255, 255, 0.06)",
                        color: "text.secondary",
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return (
                <ListItem key={`${group.id}-${item.id}`} disablePadding>
                  {wrapRailTooltip(isRailCollapsed, tooltip, listItemButton)}
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, py: 0.5 }}>
        <List disablePadding>
          <SidebarFooterAction
            isRailCollapsed={isRailCollapsed}
            title="More"
            icon={<MenuIcon />}
            onClick={handleMoreClick}
            selected={moreOpen}
            buttonProps={{
              id: "sidebar-more-button",
              "aria-label": "More",
              "aria-haspopup": "true",
              "aria-expanded": moreOpen,
              "aria-controls": moreOpen ? "sidebar-more-menu" : undefined,
            }}
          />
        </List>
        <MoreMenu
          anchorEl={moreAnchorEl}
          open={moreOpen}
          onClose={handleMoreClose}
          navItems={getMoreMenuNavItems(isAdmin)}
          onNavigate={handleMoreNavigate}
          onLogout={handleLogout}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: `1px solid ${theme.palette.divider}`,
            position: "fixed",
            height: "100vh",
            overflowX: "hidden",
            overflowY: "auto",
            backgroundColor: theme.palette.background.paper,
            zIndex: theme.zIndex.drawer + 1,
            boxShadow: isExpanded ? theme.shadows[8] : "none",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
        PaperProps={{
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            height: "100vh",
            overflow: "auto",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
